import { useEffect } from 'react';

type Profile = {
  userId?: string;
  id?: string;
  sub?: string;
  preferred_username?: string;
  name?: string;
  userName?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
};

type UserLike =
  | {
      id?: string;
      userId?: string;
      userName?: string;
      name?: string;
      email?: string;
      profile?: Profile;
    }
  | Profile
  | null
  | undefined;

type Identity = {
  userId: string;
  userName: string;
  email: string;
};

declare global {
  interface Window {
    setIdentity?: (user: Identity) => boolean | void;
    clearIdentity?: () => boolean | void;
    waitForADRUM?: (callback: () => void, timeoutMs?: number) => void;
    ADRUM?: { command?: (cmd: string, ...args: any[]) => any };
  }
}

function normalizeUser(user: UserLike): Identity | null {
  if (!user) return null;
  const anyUser = user as any;
  const p: Profile = (anyUser.profile as Profile) || (anyUser as Profile) || {};
  const id = anyUser.id || anyUser.userId || p.userId || p.id || p.sub;
  const preferred = p.preferred_username;
  const fullName = anyUser.name || anyUser.userName || p.name || p.userName;
  const given = p.given_name;
  const family = p.family_name;
  const name =
    preferred || fullName || [given, family].filter(Boolean).join(' ') || '';
  const email = anyUser.email || p.email || '';
  return {
    userId: String(id || ''),
    userName: String(name),
    email: String(email),
  };
}

export function useAppDynamics(user: UserLike, isLoggedIn: boolean) {
  useEffect(() => {
    const normalized = normalizeUser(user);
    if (isLoggedIn && normalized && window.setIdentity) {
      window.setIdentity(normalized);
    } else if (!isLoggedIn && window.clearIdentity) {
      window.clearIdentity();
    }
    return () => {
      // keep identity until explicit logout
    };
  }, [
    isLoggedIn,
    (user as any)?.profile?.sub,
    (user as any)?.profile?.email,
    (user as any)?.profile?.name,
    (user as any)?.profile?.preferred_username,
  ]);
}

export function logAppDynamicsEvent(
  eventName: string,
  eventData?: Record<string, unknown>
) {
  if (window.waitForADRUM && window.ADRUM?.command) {
    window.waitForADRUM(() => {
      try {
        if (eventData && typeof eventData === 'object') {
          Object.entries(eventData).forEach(([k, v]) => {
            window.ADRUM?.command?.(
              'addUserData',
              `evt.${k}`,
              String(v as any)
            );
          });
        }
        window.ADRUM?.command?.('addUserData', 'evt.Name', String(eventName));
        window.ADRUM?.command?.('sendBeacon');
      } catch (err) {
        if (
          typeof process !== 'undefined' &&
          process.env &&
          process.env.NODE_ENV !== 'production' &&
          typeof console !== 'undefined' &&
          typeof console.error === 'function'
        ) {
          console.error('AppDynamics event logging error:', err);
        }
      }
    });
  }
}
